import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';

const STORAGE_KEY = 'ccm-dashboard-config';

const DEFAULT_CONFIG = [
  { id: 'cameras', size: 'half', settings: {} },
  { id: 'doors', size: 'half', settings: {} },
  { id: 'alerts', size: 'half', settings: {} },
  { id: 'devices', size: 'half', settings: {} },
  { id: 'recording', size: 'full', settings: {} },
];

const useDashboardStore = create((set, get) => ({
  config: [],
  loaded: false,
  editing: false,

  loadConfig: async () => {
    try {
      const res = await fetchWithTenant('/api/tenant/user/dashboard-config');
      if (res.ok) {
        const data = await res.json();
        const cfg = Array.isArray(data.config) && data.config.length > 0
          ? data.config
          : DEFAULT_CONFIG;
        set({ config: cfg, loaded: true });
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); } catch (_) {}
        return;
      }
    } catch (_) {}

    // Fallback: localStorage, then default
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          set({ config: parsed, loaded: true });
          return;
        }
      }
    } catch (_) {}

    set({ config: DEFAULT_CONFIG, loaded: true });
  },

  saveConfig: async () => {
    const { config } = get();
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); } catch (_) {}
    try {
      await fetchWithTenant('/api/tenant/user/dashboard-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
      });
    } catch (_) {
      // localStorage already saved as fallback
    }
  },

  setEditing: (editing) => set({ editing }),

  addWidget: (id, size = 'half') => {
    set((state) => ({
      config: [...state.config, { id, size, settings: {} }],
    }));
    get().saveConfig();
  },

  removeWidget: (index) => {
    set((state) => ({
      config: state.config.filter((_, i) => i !== index),
    }));
    get().saveConfig();
  },

  // SWAP — A and B trade places (NOT insert-shift)
  swapWidgets: (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    set((state) => {
      const next = [...state.config];
      const a = next[fromIndex];
      const b = next[toIndex];
      if (!a || !b) return state;
      next[fromIndex] = b;
      next[toIndex] = a;
      return { config: next };
    });
    get().saveConfig();
  },

  toggleSize: (index) => {
    set((state) => ({
      config: state.config.map((w, i) =>
        i === index ? { ...w, size: w.size === 'full' ? 'half' : 'full' } : w
      ),
    }));
    get().saveConfig();
  },

  updateWidgetSettings: (index, settings) => {
    set((state) => ({
      config: state.config.map((w, i) =>
        i === index ? { ...w, settings: { ...(w.settings || {}), ...settings } } : w
      ),
    }));
    get().saveConfig();
  },

  resetToDefault: () => {
    set({ config: DEFAULT_CONFIG });
    get().saveConfig();
  },
}));

export { DEFAULT_CONFIG };
export default useDashboardStore;
